import React, { useState, useEffect } from 'react';
import type { Task, TaskType, TaskStatus } from '../../../../types';
import { useTaskStore } from '../../../../stores/taskStore';
import { useAuthStore } from '../../../../stores/authStore';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useGoalStore } from '../../../../stores/goalStore';
import { useTaskDisplay } from '../DragAndDrop/DragAndDropProvider';
import { useDialog } from '../../../../hooks/useDialog';
import { InlineEdit } from '../../../../components/ui/InlineEdit';
import { InlineSelector } from '../../../../components/ui/InlineSelecter';
import { InlineDatePicker } from '../../../../components/ui/InlineDatePicker';
import { TaskEditForm } from '../TaskEdit/TaskEditForm';
import { TaskDetailModal } from '../TaskDetail/TaskDetailModal';
import { getUserDisplayName, getTeamMemberDisplayName, isTextTooLong, getDisplayLength, markdownToPlain } from '../../../../utils/userUtils';
import {
  DeleteIcon,
  ClockIcon,
  PlayIcon,
  CheckIcon,
  BackIcon
} from '../../../../components/ui/Icons';
import { ChecklistRenderer } from '../../../../components/ui/ChecklistRenderer';

interface TaskCardProps {
  task: Task;
  taskType?: TaskType;
  isCompact?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  taskType,
  isCompact = false
}) => {
  const { deleteTask, updateTask, taskTypes, teamMembers } = useTaskStore();
  const { user } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const { goals } = useGoalStore();
  const { recentlyAssociatedTask, showGoalSidebar } = useTaskDisplay();
  const { confirm } = useDialog();

  // 在个人空间时应该使用空的团队成员列表
  const effectiveTeamMembers = currentWorkspace.type === 'personal' ? [] : teamMembers;
  const [isEditing, setIsEditing] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionEditValue, setDescriptionEditValue] = useState(task.description || '');

  // 点击卡片弹窗展示整个任务；卡片上自带交互的区域（按钮、下拉、日期、清单）不触发
  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, textarea, select, [data-no-open]')) return;
    setShowDetail(true);
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // 保存编辑
  const handleSaveEdit = () => {
    setIsEditing(false);
  };

  // 编辑任务标题
  const handleTitleEdit = async (newTitle: string) => {
    if (newTitle.trim() !== task.title) {
      await updateTask(task.id, { title: newTitle.trim() });
    }
  };

  // 编辑任务描述
  const handleDescriptionEdit = async (newDescription: string) => {
    if (newDescription.trim() !== (task.description || '')) {
      await updateTask(task.id, { description: newDescription.trim() });
    }
    setIsEditingDescription(false);
  };

  // 检查描述是否包含 checklist
  const hasChecklist = (description: string) => {
    if (!description) return false;
    const hasCheckboxFormat = /^(\s*)-?\s*\[\s*([ xX]?)\s*\]/m.test(description);
    // 只有写了 [ ] / [x] 才当清单在卡片上渲染；普通「- 列表」走纯文字摘要，避免卡片里满是 Markdown 符号
    return hasCheckboxFormat;
  };

  // 编辑任务类型
  const handleTaskTypeEdit = async (newTaskTypeId: string) => {
    if (newTaskTypeId !== task.taskTypeId) {
      await updateTask(task.id, { taskTypeId: newTaskTypeId });
    }
  };

  // 任务类型选择器选项
  const taskTypeOptions = taskTypes.map(type => ({
    value: type.id,
    label: type.name,
    color: type.color
  }));

  // 任务所有者选择器选项
  const ownerOptions = currentWorkspace.type === 'personal'
    ? [] // 个人空间不显示分配选择器
    : [
        { value: 'unassigned', label: '未分配' },
        ...effectiveTeamMembers.map(member => ({
          value: member.userId,
          label: getTeamMemberDisplayName(member, user?.id)
        }))
      ];

  // 任务大小选项
  const sizeOptions = [
    { value: 'SMALL', label: '小任务' },
    { value: 'MEDIUM', label: '中任务' },
    { value: 'LARGE', label: '大任务' }
  ];

  // 编辑任务大小
  const handleSizeEdit = async (newSize: string) => {
    if (newSize !== task.size) {
      await updateTask(task.id, { size: newSize as 'SMALL' | 'MEDIUM' | 'LARGE' });
    }
  };

  // 编辑任务所有者
  const handleOwnerEdit = async (newOwnerId: string) => {
    const userIdToUpdate = newOwnerId === 'unassigned' ? null : newOwnerId;
    // task中的user字段对应userId，而前端类型定义中使用assigneeId存储这个值
    const currentUserId = task.user?.id;

    if (userIdToUpdate !== currentUserId) {
      await updateTask(task.id, { userId: userIdToUpdate });
    }
  };

  // 编辑任务到期日期
  const handleDueDateEdit = async (newDueDate: string) => {
    // 修复：使用 null 而不是 undefined，避免被 JSON.stringify 过滤
    const dueDateToUpdate = newDueDate === '' ? null : newDueDate;
    const currentDueDate = task.dueDate;

    if (dueDateToUpdate !== currentDueDate) {
      await updateTask(task.id, { dueDate: dueDateToUpdate });
    }
  };


  // 获取优先级背景色
  const getPriorityBackground = () => {
    switch (task.priority) {
      case 'HIGH':
        return 'rgba(254, 226, 226, 0.4)'; // 淡红色
      case 'MEDIUM':
        return 'rgba(254, 243, 199, 0.25)'; // 淡黄色
      case 'LOW':
        return 'rgba(219, 234, 254, 0.25)'; // 淡蓝色
      default:
        return 'rgba(254, 243, 199, 0.25)'; // 默认中优先级
    }
  };

  // 获取任务大小文字
  const getSizeText = () => {
    switch (task.size) {
      case 'SMALL':
        return '小';
      case 'MEDIUM':
        return '中';
      case 'LARGE':
        return '大';
      default:
        return '中';
    }
  };


  // 状态变更处理函数
  const handleStatusChange = async (newStatus: TaskStatus, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await updateTask(task.id, { status: newStatus });
    } catch (error) {
      console.error('更新任务状态失败:', error);
    }
  };

  // 删除任务
  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = await confirm({
      title: '删除任务',
      message: '确定要删除这个任务吗？',
      confirmText: '删除',
      cancelText: '取消',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        await deleteTask(task.id);
      } catch (error) {
        console.error('删除任务失败:', error);
      }
    }
  };

  // 格式化日期
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric'
    });
  };

  // 计算持续时间
  const calculateDuration = () => {
    if (!task.startedAt || !task.completedAt) {
      return '';
    }

    const startTime = new Date(task.startedAt);
    const endTime = new Date(task.completedAt);
    const diffMs = endTime.getTime() - startTime.getTime();

    // 计算天数和小时数
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays >= 1) {
      return `${diffDays}天`;
    } else {
      return `${diffHours}小时`;
    }
  };

  // 判断是否过期
  const isOverdue = () => {
    if (!task.dueDate) return false;
    return new Date(task.dueDate) < new Date() && task.status !== 'DONE';
  };

  // 获取关联的目标
  const getAssociatedGoal = () => {
    if (!task.goalId) return null;
    return goals.find(goal => goal.id === task.goalId);
  };

  // 渲染状态操作按钮
  const renderStatusButtons = () => {
    const buttons = [];
    
    if (task.status === 'TODO') {
      // 待办状态：只有开始按钮
      buttons.push(
        <button
          key="start"
          onClick={(e) => handleStatusChange('IN_PROGRESS', e)}
          className="status-btn w-6 h-6 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white flex items-center justify-center transition-all duration-200"
          title="开始任务"
        >
          <PlayIcon size={12} />
        </button>
      );
    } else if (task.status === 'IN_PROGRESS') {
      // 进行中状态：只有完成按钮
      buttons.push(
        <button
          key="complete"
          onClick={(e) => handleStatusChange('DONE', e)}
          className="status-btn w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-all duration-200"
          title="完成任务"
        >
          <CheckIcon size={12} />
        </button>
      );
    } else if (task.status === 'REVIEW') {
      // 待验收：打回 / 通过（只有人能把任务标为已完成）
      buttons.push(
        <button
          key="reject"
          onClick={(e) => handleStatusChange('IN_PROGRESS', e)}
          className="status-btn w-6 h-6 rounded-full bg-amber-400 hover:bg-amber-500 text-white flex items-center justify-center transition-all duration-200"
          title="打回，让 agent 继续做"
        >
          <BackIcon size={12} />
        </button>,
        <button
          key="approve"
          onClick={(e) => handleStatusChange('DONE', e)}
          className="status-btn w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-all duration-200"
          title="验收通过，标为已完成"
        >
          <CheckIcon size={12} />
        </button>
      );
    } else if (task.status === 'BLOCKED') {
      buttons.push(
        <button
          key="resume"
          onClick={(e) => handleStatusChange('IN_PROGRESS', e)}
          className="status-btn w-6 h-6 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white flex items-center justify-center transition-all duration-200"
          title="补充信息后重新开始"
        >
          <PlayIcon size={12} />
        </button>
      );
    }

    return buttons;
  };

  // 已完成任务的两行布局
  if (task.status === 'DONE') {
    return (
      <div
        className={`
          task-card completed group px-5 py-3.5 rounded-lg transition-all duration-200 cursor-grab relative
          ${isCompact ? 'px-3 py-2' : ''}
        `}
        data-type={taskType?.name}
        data-task-id={task.id}
        onClick={handleCardClick}
      >
        {/* 右上角删除按钮 - 一半浮于卡片之外 */}
        <button
          onClick={handleDelete}
          className="absolute -top-2 -right-2 w-5 h-5 bg-neutral-25 border border-neutral-100 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg hover:shadow-xl z-10"
          title="删除任务"
        >
          <DeleteIcon size={14} />
        </button>

        {/* 右上角持续时间 */}
        <div className="absolute top-2 right-2">
          <div className="flex items-center gap-1 text-xs bg-neutral-25/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm border border-neutral-100 text-gray-500">
            <ClockIcon size={10} />
            <span>{calculateDuration() || formatDate(task.taskDate || task.updatedAt)}</span>
          </div>
        </div>

        {/* 第一行：任务标题（带删除线） */}
        <div className="flex items-start mb-1.5 pr-12">
          {/* 任务标题（带删除线）和目标徽章 */}
          <div className="flex-1 min-w-0">
            <h4 className={`
              font-medium text-gray-600 line-through leading-tight truncate w-full
              ${isCompact ? 'text-sm' : 'text-base'}
            `}
            title={task.title}>
              {task.title}
            </h4>

            {/* 目标徽章 - 只在目标侧边栏打开时显示 */}
            {getAssociatedGoal() && showGoalSidebar && (
              <div className="flex items-center gap-1 mt-1">
                <div className={`
                  inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full transition-all duration-500
                  ${recentlyAssociatedTask === task.id
                    ? 'bg-green-200 text-green-800 ring-2 ring-green-300 animate-pulse'
                    : 'bg-blue-100 text-blue-700'
                  }
                `}>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{getAssociatedGoal()?.title}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 第二行：底部信息栏（与其他卡片保持一致） */}
        <div className="flex justify-between items-center">
          {/* 左下角任务所有者 - 仅在团队空间显示 */}
          <div className="flex items-center">
            {currentWorkspace.type === 'team' && (
              <div className="owner-area flex items-center text-xs text-gray-500"
                   title={`负责人：${task.user?.id ? getUserDisplayName(task.user) : '未分配'}`}>
                {task.user?.id ? (
                  // 已分配：显示用户名
                  <span className="text-xs text-gray-700 font-medium">
                    {getUserDisplayName(task.user)}
                  </span>
                ) : (
                  // 未分配：显示默认人员图标
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                       className="text-gray-400">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                )}
              </div>
            )}
          </div>

          {/* 右下角任务类型和大小 */}
          <div className="flex items-center gap-0.5">
            {/* 任务大小圆点 */}
            <div
              className={`
                rounded-full
                ${task.size === 'SMALL' ? 'w-1.5 h-1.5' : task.size === 'LARGE' ? 'w-3 h-3' : 'w-2 h-2'}
              `}
              style={{ backgroundColor: taskType?.color || '#6B7280' }}
              title={`${taskType?.name || '未分类'} - ${getSizeText()}任务`}
            />

            {/* 任务类型 - 仅在有类型时显示 */}
            {taskType && (
              <span className="text-xs text-gray-600">{taskType.name}</span>
            )}
          </div>
        </div>

        {showDetail && <TaskDetailModal task={task} onClose={() => setShowDetail(false)} />}
      </div>
    );
  }

  // 如果正在编辑，显示编辑表单
  if (isEditing) {
    return (
      <TaskEditForm
        task={task}
        taskType={taskType}
        onCancel={handleCancelEdit}
        onSave={handleSaveEdit}
      />
    );
  }

  // 待办、进行中、待验收、受阻任务的完整布局
  return (
    <div
      className={`
        task-card group px-5 py-3.5 rounded-lg transition-all duration-200 cursor-grab relative
        ${isCompact ? 'px-3 py-2' : ''}
        ${task.status === 'REVIEW' ? 'border-l-4 border-amber-400' : ''}
        ${task.status === 'BLOCKED' ? 'border-l-4 border-red-400' : ''}
      `}
      style={{
        backgroundColor: getPriorityBackground()
      }}
      data-type={taskType?.name}
      data-task-id={task.id}
      onClick={handleCardClick}
    >

      {/* 关联成功提示 - 浮动在任务卡片上方 */}
      {recentlyAssociatedTask === task.id && (
        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg animate-bounce">
            ✓ 已关联到目标
          </div>
        </div>
      )}

      {/* 右上角删除按钮 - 一半浮于卡片之外 */}
      <button
        onClick={handleDelete}
        className="absolute -top-2 -right-2 w-5 h-5 bg-neutral-25 border border-neutral-100 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg hover:shadow-xl z-10"
        title="删除任务"
      >
        <DeleteIcon size={14} />
      </button>


      {/* 右上角截止日期 */}
      <div className="absolute top-2 right-2" data-no-open>
        <InlineDatePicker
          value={task.dueDate}
          onSave={handleDueDateEdit}
          placeholder="设置日期"
          className=""
          renderDisplay={(value) => (
            <div className={`
              flex items-center gap-1 text-xs cursor-pointer hover:opacity-90 transition-all
              px-2 py-1 rounded-md shadow-sm
              ${isOverdue()
                ? 'bg-red-50 text-red-600 border border-red-300 font-semibold'
                : 'bg-neutral-25/80 backdrop-blur-sm border border-neutral-100 text-gray-500'
              }
              ${!value ? 'opacity-0 group-hover:opacity-100' : ''}
            `}>
              {isOverdue() && value ? (
                <>
                  <span className="text-sm">⚠</span>
                  <span>{formatDate(value)}</span>
                </>
              ) : (
                <>
                  <ClockIcon size={10} />
                  <span>{value ? formatDate(value) : '设置'}</span>
                </>
              )}
            </div>
          )}
        />
      </div>
      
      {/* 标题 */}
      <div className="mb-3 pr-12" title={task.title}>
        <InlineEdit
          value={task.title}
          onSave={handleTitleEdit}
          placeholder="任务标题..."
          className={`
            font-medium text-gray-700 leading-tight
            ${isCompact ? 'text-sm' : 'text-base'}
          `}
          displayClassName="truncate w-full"
          disabled
          validateLength={(text) => !isTextTooLong(text)}
          lengthMessage="标题过长！中文字符不超过18个，英文字符不超过36个"
          getDisplayLength={getDisplayLength}
          maxDisplayLength={18}
        />

        {/* 目标徽章 - 只在目标侧边栏打开时显示 */}
        {getAssociatedGoal() && showGoalSidebar && (
          <div className="flex items-center gap-1 mt-2">
            <div className={`
              inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full transition-all duration-500
              ${recentlyAssociatedTask === task.id
                ? 'bg-green-200 text-green-800 ring-2 ring-green-300 animate-pulse'
                : 'bg-blue-100 text-blue-700'
              }
            `}>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{getAssociatedGoal()?.title}</span>
            </div>
          </div>
        )}
      </div>
      
      {/* 描述 - 只在有内容时显示 */}
      {(task.description && task.description.trim()) && (
        <div className="mb-1.5">
          {!isEditingDescription && hasChecklist(task.description) ? (
            // 查看模式 + 包含 checklist：使用 ChecklistRenderer
            <div data-no-open className="cursor-pointer max-h-40 overflow-y-auto">
              <ChecklistRenderer
                content={task.description}
                onUpdate={async (newContent) => {
                  await updateTask(task.id, { description: newContent });
                }}
              />
            </div>
          ) : isEditingDescription ? (
            // 编辑模式：显示文本框
            <div className="relative">
              <textarea
                value={descriptionEditValue}
                onChange={(e) => {
                  setDescriptionEditValue(e.target.value);
                }}
                onBlur={async () => {
                  // 失去焦点时保存
                  if (descriptionEditValue.trim() !== task.description) {
                    await handleDescriptionEdit(descriptionEditValue);
                  }
                  setIsEditingDescription(false);
                }}
                onKeyDown={async (e) => {
                  if (e.key === 'Escape') {
                    setDescriptionEditValue(task.description || '');
                    setIsEditingDescription(false);
                  } else if (e.key === 'Enter' && e.ctrlKey) {
                    e.preventDefault();
                    // Ctrl+Enter 保存并退出
                    if (descriptionEditValue.trim() !== task.description) {
                      await handleDescriptionEdit(descriptionEditValue);
                    } else {
                      setIsEditingDescription(false);
                    }
                  }
                }}
                onMouseDown={(e) => {
                  // 阻止拖拽事件，允许文本选择
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  // 阻止拖拽事件，允许文本选择
                  e.stopPropagation();
                }}
                autoFocus
                className={`
                  w-full p-2 border border-blue-400 rounded-md resize-none
                  text-gray-600 leading-normal
                  ${isCompact ? 'text-xs' : 'text-sm'}
                  focus:outline-none focus:ring-2 focus:ring-blue-300
                `}
                rows={8}
                maxLength={20000}
                placeholder="添加任务描述..."
              />
              <div className="text-xs text-gray-400 mt-1">
                Ctrl+Enter 保存 • ESC 取消 • 双击打开详细编辑
              </div>
            </div>
          ) : (
            // 查看模式 + 无 checklist：普通文本
            <div
              className={`
                text-gray-600 leading-normal cursor-pointer hover:bg-gray-50 rounded p-1 whitespace-pre-wrap line-clamp-4
                ${isCompact ? 'text-xs' : 'text-sm'}
              `}
            >
              {markdownToPlain(task.description)}
            </div>
          )}
        </div>
      )}
      
      {/* 底部信息栏：左下角任务负责人，右下角状态按钮、任务类型和大小 */}
      <div className="flex justify-between items-center flex-wrap gap-y-1">
        {/* 左下角任务所有者 - 仅在团队空间显示 */}
        <div className="flex items-center" data-no-open>
          {currentWorkspace.type === 'team' && (
            <InlineSelector
              value={task.user?.id || 'unassigned'}
              options={ownerOptions}
              onSave={handleOwnerEdit}
              renderDisplay={(option) => (
                <div className="owner-area flex items-center text-xs text-gray-500 cursor-pointer hover:bg-gray-50 rounded p-1 -m-1"
                     title={`负责人：${option?.label || '未分配'}`}>
                  {task.user?.id ? (
                    // 已分配：显示用户名
                    <span className="text-xs text-gray-700 font-medium whitespace-nowrap">
                      {option?.label || getUserDisplayName(task.user)}
                      {task.user.isAgent && (
                        <span className="ml-1 px-1 rounded bg-indigo-100 text-indigo-700 font-normal">AI</span>
                      )}
                    </span>
                  ) : (
                    // 未分配：显示默认人员图标
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="text-gray-400">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  )}
                </div>
              )}
            />
          )}
        </div>
        
        {/* 右下角状态按钮、任务类型和大小 */}
        <div className="flex items-center gap-1 ml-auto" data-no-open>
          {/* 状态操作按钮 */}
          <div className={`flex gap-1 transition-opacity duration-200 ${
            task.status === 'REVIEW' ? '' : 'opacity-0 group-hover:opacity-100'
          }`}>
            {renderStatusButtons()}
          </div>
          
          {/* 任务大小和类型组合 */}
          <div className="flex items-center gap-0.5">
            {/* 任务大小选择器（圆点） */}
            <InlineSelector
              value={task.size || 'MEDIUM'}
              options={sizeOptions}
              onSave={handleSizeEdit}
              renderDisplay={(option) => (
                <div 
                  className={`
                    rounded-full cursor-pointer hover:opacity-80
                    ${task.size === 'SMALL' ? 'w-1.5 h-1.5' : task.size === 'LARGE' ? 'w-3 h-3' : 'w-2 h-2'}
                  `}
                  style={{ backgroundColor: taskType?.color || '#6B7280' }}
                  title={`${taskType?.name || '未分类'} - ${getSizeText()}任务`}
                />
              )}
            />

            {/* 任务类型选择器 */}
            <InlineSelector
              value={task.taskTypeId}
              options={taskTypeOptions}
              onSave={handleTaskTypeEdit}
              renderDisplay={(option) => (
                option?.label ? (
                  <span className="text-xs text-gray-600">{option.label}</span>
                ) : null
              )}
            />
          </div>
        </div>
      </div>

      {showDetail && <TaskDetailModal task={task} onClose={() => setShowDetail(false)} />}
    </div>
  );
};
